import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightOutlined } from "@ant-design/icons";

import "./default-section.component.styles.scss";
import { conversions } from "../../../lib/analytics";
import { useLang } from "../../../lib/i18n";
import { useReveal } from "../../../lib/motion";

interface IDefaultSectionProps {
  imageUrl: string;
  /**
   * Descriptive alt text. Falls back to title + eyebrow when omitted,
   * but providing an explicit one is strongly recommended for SEO.
   */
  imageAlt?: string;
  eyebrow?: string;
  index?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  orientation: "left" | "right";
  to?: string;
}

const DefaultSection: React.FC<IDefaultSectionProps> = ({
  imageUrl,
  imageAlt,
  orientation,
  eyebrow,
  index,
  title,
  description,
  buttonText,
  to = "/booking",
}) => {
  const { lang, lp } = useLang();
  const mediaRef = useReveal<HTMLDivElement>();
  const bodyRef = useReveal<HTMLDivElement>();
  const handleClick = () => {
    if (to === "/booking") {
      conversions.book(`section_${index ?? eyebrow ?? "default"}`, eyebrow);
    }
  };
  const resolvedTo = to.startsWith("/") ? lp(to) : to;
  const ctaTitle =
    lang === "en"
      ? "Laviens — book a moment of well-being with us."
      : "Laviens — Prenota un momento di benessere con noi.";

  const resolvedAlt =
    imageAlt ||
    [eyebrow, title].filter(Boolean).join(" — ") ||
    "Laviens Studio Olistico";

  return (
    <article
      className={`lvs-editorial lvs-editorial--${orientation}`}
      itemProp="service"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Schema.org microdata: expose service name/description even without JS */}
      {title && <meta itemProp="name" content={title} />}
      {description && <meta itemProp="description" content={description} />}
      <meta itemProp="provider" content="Laviens — Studio Olistico" />
      <meta itemProp="areaServed" content="Firenze, Sesto Fiorentino" />

      <div className="lvs-editorial__media lvs-reveal" ref={mediaRef}>
        {/* Keep the CSS bg for the art-directed visual, but also render a
            real <img> (visually hidden when bg is used) so every crawler
            — including non-JS AI bots — gets the alt text and URL. */}
        <div
          className="lvs-editorial__image"
          style={{ backgroundImage: `url(${imageUrl})` }}
          aria-hidden="true"
        />
        <img
          src={imageUrl}
          alt={resolvedAlt}
          loading="lazy"
          decoding="async"
          className="lvs-editorial__imageSr"
          itemProp="image"
        />
        {index && <span className="lvs-editorial__index">{index}</span>}
      </div>
      <div
        className="lvs-editorial__body lvs-reveal"
        ref={bodyRef}
        style={{ "--lvs-d": "90ms" } as React.CSSProperties}
      >
        {eyebrow && <span className="eyebrow lvs-editorial__eyebrow">{eyebrow}</span>}
        {title && <h2 className="lvs-editorial__title">{title}</h2>}
        {description && <p className="lvs-editorial__desc">{description}</p>}
        {buttonText && (
          <Link
            to={resolvedTo}
            className="lvs-editorial__cta"
            title={ctaTitle}
            onClick={handleClick}
          >
            {buttonText}
            <ArrowRightOutlined />
          </Link>
        )}
      </div>
    </article>
  );
};

export default DefaultSection;
